// ─── JOURNAL DE BORD du calendrier (calque plein écran) ───────────
// Notes datées partagées : fil chronologique + ＋ en-tête + swipe ✏️/🗑️ +
// tap = fiche en lecture. Porté de MoPCal (`JournalScreen`), avec trois
// différences assumées : l'ÉDITION d'une note existe (MoPCal exposait
// `modifierNote` sans jamais l'appeler), la DATE est choisissable (MoPCal
// forçait le jour même, alors que la fonction s'appelle « notes datées »),
// et l'erreur d'écoute est AFFICHÉE au lieu d'être avalée.
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, ScrollView, StyleSheet } from 'react-native';
import {
  PageHeader, EmptyState, LigneSwipe, GesteRetourEcran, SelecteurDate, SelecteurHeure,
  stylesForm, useUI, useT, useAuth, confirmerSuppression, frJMA, HIT,
} from '@mop/core';
import { ecouterNotes, ajouterNote, modifierNote, supprimerNote } from './services/journal';
import { emojiNote, labelValeur, labelNature, natureDe, heureNote, notesDuJour,
  jourVoisin, decalerJour, aujourdHui } from './data/journal';
import FeuilleNote from './FeuilleNote';
import FicheNoteInfo from './FicheNoteInfo';

export default function EcranJournal({ calId, onFermer }) {
  const UI = useUI();
  const t = useT();
  const { user } = useAuth();
  const s = useMemo(() => makeStyles(UI), [UI]);

  const [notes, setNotes]   = useState([]);
  const [charge, setCharge] = useState(true);
  // Deux erreurs distinctes : la LECTURE (bandeau permanent — la liste ne
  // reflète alors plus la base) et l'ÉCRITURE (dans la feuille, à côté du
  // geste qui a échoué). Les confondre masquerait la première.
  const [erreurLecture, setErreurLecture] = useState('');
  const [erreurEcriture, setErreurEcriture] = useState('');

  const [feuille, setFeuille] = useState(null);
  // Vue JOUR (demande de Cedric) : une journee a la fois, avec navigation
  // avant/apres comme la vue jour du calendrier. La liste complete reste
  // accessible — on ajoute un mode, on n'en retire pas un.
  const [modeJour, setModeJour] = useState(true);
  const [jour, setJour] = useState(aujourdHui);   // { initial } | null
  const [fiche, setFiche]     = useState(null);   // note affichée en lecture
  const [dateReq, setDateReq] = useState(null);   // { valeur, onChoisir }
  const [heureReq, setHeureReq] = useState(null); // idem, pour l'heure saisie

  useEffect(() => {
    if (!calId) { setNotes([]); setCharge(false); return; }
    setCharge(true); setErreurLecture('');
    const stop = ecouterNotes(
      calId,
      (l) => { setNotes(l); setCharge(false); },
      (msg) => { setErreurLecture(msg); setCharge(false); },
    );
    return () => stop();
  }, [calId]);

  // Jour affiche : celui choisi s'il porte encore des notes, sinon le plus
  // recent. Sans ce repli, supprimer la derniere note d'une journee laissait
  // un ecran vide dont on ne pouvait plus sortir.
  // Le jour affiche est celui CHOISI, meme s'il est vide : la navigation passe
  // « de jour en jour meme vides » (Cedric). Les DOUBLES fleches sautent, elles,
  // au prochain jour qui porte des notes.
  const jourVu = jour || aujourdHui();
  const duJour = useMemo(() => notesDuJour(notes, jourVu), [notes, jourVu]);
  const avant = decalerJour(jourVu, -1);
  const apres = decalerJour(jourVu, +1);
  const sautAvant = jourVoisin(notes, jourVu, -1);
  const sautApres = jourVoisin(notes, jourVu, +1);
  const estAujourdHui = jourVu === aujourdHui();
  const affichees = modeJour ? duJour : notes;

  const enregistrer = async (note) => {
    setErreurEcriture('');
    try {
      if (feuille?.initial?.id) {
        await modifierNote(feuille.initial.id, note);
      } else {
        await ajouterNote({
          calendrierId: calId,
          auteurUid: user?.uid,
          auteurNom: user?.prenom || user?.email || '',
          ...note,
        });
      }
      setFeuille(null);
    } catch (e) {
      setErreurEcriture(e?.message || String(e));
    }
  };

  const supprimer = (n) => confirmerSuppression(
    t('journal.supprimerTitre'), t('journal.supprimerMsg'),
    async () => { try { await supprimerNote(n.id); } catch (e) { setErreurLecture(e?.message || String(e)); } },
  );

  return (
    <GesteRetourEcran actif onRetour={onFermer}>
      <View style={{ flex: 1, backgroundColor: UI.bg }}>
        <PageHeader titre={t('journal.titre')} iconeTitre="📔" onRetour={onFermer}
          action={{ icone: '＋', label: t('journal.nouvelleNote'), onPress: () => { setErreurEcriture(''); setFeuille({ initial: null }); } }}>
          <TouchableOpacity onPress={() => setModeJour(v => !v)} hitSlop={HIT}
            accessibilityLabel={t(modeJour ? 'journal.voirTout' : 'journal.voirJour')}>
            <Text style={s.bascule}>{modeJour ? '📋' : '🗓️'}</Text>
          </TouchableOpacity>
        </PageHeader>

        {modeJour && !!jourVu && (
          <View style={s.navJour}>
            {/* Fleche desactivee quand il n'y a plus de jour AVEC des notes de
                ce cote : naviguer vers du vide n'apprend rien. */}
            <View style={s.groupeFleches}>
              {/* Double fleche : saute au prochain jour AYANT des notes.
                  Desactivee quand il n'y en a plus de ce cote. */}
              <TouchableOpacity onPress={() => setJour(sautAvant)} disabled={!sautAvant}
                hitSlop={HIT} accessibilityLabel={t('journal.jourNotePrec')}>
                <Text style={[s.fleche, s.double, !sautAvant && s.flecheOff]}>«</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => setJour(avant)} hitSlop={HIT}
                accessibilityLabel={t('journal.jourPrec')}>
                <Text style={s.fleche}>‹</Text>
              </TouchableOpacity>
            </View>

            <TouchableOpacity onPress={() => setJour(aujourdHui())} disabled={estAujourdHui}
              hitSlop={HIT} accessibilityLabel={t('journal.aujourdhui')}>
              <Text style={s.jourTitre}>{frJMA(jourVu)}</Text>
              {!estAujourdHui && <Text style={s.retourAuj}>{t('journal.aujourdhui')}</Text>}
            </TouchableOpacity>

            <View style={s.groupeFleches}>
              <TouchableOpacity onPress={() => setJour(apres)} hitSlop={HIT}
                accessibilityLabel={t('journal.jourSuiv')}>
                <Text style={s.fleche}>›</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => setJour(sautApres)} disabled={!sautApres}
                hitSlop={HIT} accessibilityLabel={t('journal.jourNoteSuiv')}>
                <Text style={[s.fleche, s.double, !sautApres && s.flecheOff]}>»</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}

        <ScrollView contentContainerStyle={s.corps}>
          {/* Une écoute refusée ne doit JAMAIS passer pour « aucune note » :
              c'est ce qui a fait croire en juin qu'une ligne enregistrée
              n'existait pas (cf. COWORK.md, correctif `estCal`). */}
          {!!erreurLecture && (
            <View style={s.bandeau}>
              <Text style={s.bandeauTxt}>⚠️ {t('journal.erreurLecture')}</Text>
              <Text style={s.bandeauDet}>{erreurLecture}</Text>
            </View>
          )}

          {affichees.length === 0 && !erreurLecture ? (
            charge ? null : <EmptyState icon="📔" texte={t('journal.aucuneNote')} aide={t('journal.aucuneAide')} />
          ) : affichees.map(n => {
            const valeur = labelValeur(n, t);
            return (
              <LigneSwipe key={n.id} espacement={8} enabled
                onEditer={() => { setErreurEcriture(''); setFeuille({ initial: n }); }}
                onSupprimer={() => supprimer(n)}>
                <TouchableOpacity style={s.carte} activeOpacity={0.7} onPress={() => setFiche(n)}>
                  <Text style={s.emoji}>{emojiNote(n)}</Text>
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <View style={s.ligneHaut}>
                      <Text style={s.date}>
                        {modeJour ? heureNote(n) : `${frJMA(n.date)}${heureNote(n) ? ' · ' + heureNote(n) : ''}`}
                      </Text>
                      <Text style={s.etiquette} numberOfLines={1}>
                        {valeur || labelNature(natureDe(n), t)}
                      </Text>
                    </View>
                    {!!n.auteurNom && <Text style={s.auteur} numberOfLines={1}>{n.auteurNom}</Text>}
                  </View>
                  {/* Le contenu occupe la MOITIE DROITE, jusqu'ici vide. Tronque
                      a la hauteur de la carte : le tap ouvre la fiche entiere. */}
                  {!!n.texte && (
                    <Text style={s.texteDroite} numberOfLines={3} ellipsizeMode="tail">
                      {n.texte}
                    </Text>
                  )}
                </TouchableOpacity>
              </LigneSwipe>
            );
          })}
        </ScrollView>

        <FeuilleNote visible={!!feuille} initial={feuille?.initial} erreur={erreurEcriture}
          onDemanderDate={setDateReq} onDemanderHeure={setHeureReq}
          onFermer={() => setFeuille(null)} onEnregistrer={enregistrer} />

        {/* Fiche et feuille sont EXCLUSIVES : passer à l'édition ferme la
            fiche dans le même rendu — jamais deux feuilles superposées. */}
        <FicheNoteInfo visible={!!fiche} note={fiche} auteur={fiche?.auteurNom}
          onFermer={() => setFiche(null)}
          onEditer={() => { const n = fiche; setFiche(null); setErreurEcriture(''); setFeuille({ initial: n }); }} />

        {/* Sélecteur de date rendu au niveau de l'ÉCRAN (jamais imbriqué dans
            la feuille de note) — même montage que EcranContacts. */}
        <SelecteurDate visible={!!dateReq} valeur={dateReq?.valeur}
          onChange={(dk) => dateReq?.onChoisir(dk)} onFermer={() => setDateReq(null)} />
        {/* `avecEffacer` : l'heure est FACULTATIVE — on doit pouvoir revenir a
            « pas d'heure saisie », et donc au repli sur l'horodatage. */}
        <SelecteurHeure visible={!!heureReq} valeur={heureReq?.valeur} avecEffacer
          onChange={(hm) => heureReq?.onChoisir(hm)} onFermer={() => setHeureReq(null)} />
      </View>
    </GesteRetourEcran>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  bascule: { fontSize: 19, marginLeft: 10 },
  navJour: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 18, paddingVertical: 8,
    borderBottomWidth: 1, borderBottomColor: UI.border, backgroundColor: UI.card,
  },
  jourTitre: { fontSize: 15, fontWeight: '800', color: UI.ink, textAlign: 'center' },
  retourAuj: { fontSize: 10.5, color: UI.accent, textAlign: 'center', marginTop: 1 },
  groupeFleches: { flexDirection: 'row', alignItems: 'center' },
  double: { fontSize: 20 },
  texteDroite: { flex: 1, minWidth: 0, fontSize: 13.5, color: UI.ink, lineHeight: 18, marginLeft: 10 },
  fleche: { fontSize: 26, color: UI.accent, paddingHorizontal: 10 },
  flecheOff: { opacity: 0.25 },
  ...stylesForm(UI),
  corps:      { padding: 12, paddingBottom: 30 },
  carte:      { flexDirection: 'row', alignItems: 'flex-start', gap: 10, backgroundColor: UI.card, borderRadius: 13, borderWidth: 0.5, borderColor: UI.border, padding: 12 },
  emoji:      { fontSize: 18, marginTop: 1 },
  ligneHaut:  { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 3 },
  date:       { fontSize: 13.5, fontWeight: '800', color: UI.ink },
  etiquette:  { flex: 1, fontSize: 12, color: UI.accent, fontWeight: '700' },
  texte:      { fontSize: 13.5, color: UI.ink, lineHeight: 19 },
  auteur:     { fontSize: 11.5, color: UI.faint, marginTop: 4 },
  bandeau:    { backgroundColor: UI.dangerTint || '#fdecec', borderRadius: 12, borderWidth: 0.5, borderColor: UI.danger || '#e5484d', padding: 11, marginBottom: 10 },
  bandeauTxt: { fontSize: 13, fontWeight: '800', color: UI.danger || '#e5484d' },
  bandeauDet: { fontSize: 11.5, color: UI.muted, marginTop: 3 },
});
